export interface ToolBarCallbacks {
  onSpeedChange: (speed: number) => void;
  onScaleIncrease: () => void;
  onScaleDecrease: () => void;
  onModeToggle: () => void;
  onSaveSlot: () => void;
  onStartPause: () => void;
  onStep: () => void;
  onReset: () => void;
  onDebugToggle: () => void;
}

const SPEED_OPTIONS = [0.5, 1, 2, 4] as const;

export class ToolBar {
  readonly el: HTMLDivElement;
  private callbacks: ToolBarCallbacks;
  private scaleLabel: HTMLSpanElement;
  private modeLabel: HTMLSpanElement;
  private speedButtons: Map<number, HTMLButtonElement> = new Map();
  private runningLabel: HTMLSpanElement;
  private debugLabel: HTMLSpanElement;

  constructor(container: HTMLElement, initialScale: number, initialMode: string, callbacks: ToolBarCallbacks) {
    this.callbacks = callbacks;

    this.el = document.createElement('div');
    this.el.className = 'toolbar';

    const runGroup = document.createElement('div');
    runGroup.className = 'toolbar-group';

    const startPauseBtn = document.createElement('button');
    startPauseBtn.textContent = 'Start';
    startPauseBtn.onclick = () => this.callbacks.onStartPause();
    runGroup.appendChild(startPauseBtn);

    const stepBtn = document.createElement('button');
    stepBtn.textContent = 'Step';
    stepBtn.onclick = () => this.callbacks.onStep();
    runGroup.appendChild(stepBtn);

    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset';
    resetBtn.onclick = () => this.callbacks.onReset();
    runGroup.appendChild(resetBtn);

    this.runningLabel = document.createElement('span');
    this.runningLabel.className = 'running-label';
    this.runningLabel.textContent = 'Stopped';
    runGroup.appendChild(this.runningLabel);

    this.el.appendChild(runGroup);

    const speedGroup = document.createElement('div');
    speedGroup.className = 'toolbar-group';
    const speedTitle = document.createElement('span');
    speedTitle.textContent = 'Speed:';
    speedGroup.appendChild(speedTitle);

    for (const speed of SPEED_OPTIONS) {
      const btn = document.createElement('button');
      btn.textContent = speed >= 1 ? `${speed}x` : `${speed}x`;
      btn.className = speed === 1 ? 'active' : '';
      btn.onclick = () => {
        this.setActiveSpeed(speed);
        this.callbacks.onSpeedChange(speed);
      };
      this.speedButtons.set(speed, btn);
      speedGroup.appendChild(btn);
    }

    this.el.appendChild(speedGroup);

    const scaleGroup = document.createElement('div');
    scaleGroup.className = 'toolbar-group';
    const scaleTitle = document.createElement('span');
    scaleTitle.textContent = 'Scale:';
    scaleGroup.appendChild(scaleTitle);

    const decBtn = document.createElement('button');
    decBtn.textContent = '-';
    decBtn.onclick = () => this.callbacks.onScaleDecrease();
    scaleGroup.appendChild(decBtn);

    this.scaleLabel = document.createElement('span');
    this.scaleLabel.textContent = String(initialScale) + 'x';
    this.scaleLabel.className = 'scale-label';
    scaleGroup.appendChild(this.scaleLabel);

    const incBtn = document.createElement('button');
    incBtn.textContent = '+';
    incBtn.onclick = () => this.callbacks.onScaleIncrease();
    scaleGroup.appendChild(incBtn);

    this.el.appendChild(scaleGroup);

    const modeGroup = document.createElement('div');
    modeGroup.className = 'toolbar-group';

    const modeBtn = document.createElement('button');
    this.modeLabel = document.createElement('span');
    this.modeLabel.textContent = initialMode;
    modeBtn.appendChild(this.modeLabel);
    modeBtn.onclick = () => this.callbacks.onModeToggle();
    modeGroup.appendChild(modeBtn);

    this.el.appendChild(modeGroup);

    const saveGroup = document.createElement('div');
    saveGroup.className = 'toolbar-group';
    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Save State';
    saveBtn.onclick = () => this.callbacks.onSaveSlot();
    saveGroup.appendChild(saveBtn);
    this.el.appendChild(saveGroup);

    const debugGroup = document.createElement('div');
    debugGroup.className = 'toolbar-group';

    const debugBtn = document.createElement('button');
    debugBtn.textContent = 'Debug';
    debugBtn.onclick = () => this.callbacks.onDebugToggle();
    debugGroup.appendChild(debugBtn);

    this.debugLabel = document.createElement('span');
    this.debugLabel.textContent = 'OFF';
    debugGroup.appendChild(this.debugLabel);

    this.el.appendChild(debugGroup);

    container.appendChild(this.el);
  }

  updateScale(scale: number): void {
    this.scaleLabel.textContent = String(Math.round(scale * 100) / 100) + 'x';
  }

  updateMode(mode: string): void {
    this.modeLabel.textContent = mode;
  }

  updateRunning(running: boolean): void {
    this.runningLabel.textContent = running ? 'Running' : 'Stopped';
  }

  updateDebugState(enabled: boolean): void {
    this.debugLabel.textContent = enabled ? 'ON' : 'OFF';
  }

  private setActiveSpeed(speed: number): void {
    for (const [s, btn] of this.speedButtons) {
      btn.className = s === speed ? 'active' : '';
    }
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
